import React, { createContext, useState, ReactNode, useContext } from "react";

interface User {
  access_token: string;
  refresh_token?: string;
  [key: string]: any; // Allow additional properties if needed
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (user: User, from: string) => void;
  logout: () => void;
  catalogData: (data: any) => void;
  catalog: any[];
  user: User | null;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Retrieve stored user from localStorage
  const storedUser = localStorage.getItem("usmonUser");
  const parsedUser: User | null = storedUser ? JSON.parse(storedUser) : null;
  const isUserAuthenticated = !!parsedUser?.access_token;
  const [user, setUser] = useState<User | null>(parsedUser);


  // State for authentication status
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(isUserAuthenticated);
  const [catalog, setCatalog] = useState<any[]>(() => {
  try {
      const stored = localStorage.getItem("usmonCatalog");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const determineUserRole = (userObj: any) => {
    if (userObj?.user?.email?.endsWith("@usmon.com")) {
      return "usmon-admin";
    } else if (userObj?.user?.insightsRole === "Admin") {
      return "client-admin";
    }
    return "user";
  };

  const login = (user: User, from: string) => {
  if (from === "login") {
    const isUserAdmin = determineUserRole(user);
    const updatedUser = {
      ...user,
      isUserAdmin,
    };

    localStorage.setItem("usmonUser", JSON.stringify(updatedUser));
    setUser(updatedUser);
    setIsAuthenticated(!!user.access_token);

  } else if (from === "switch-client") {
    const storedUser = localStorage.getItem("usmonUser");
    const parsedUser = storedUser ? JSON.parse(storedUser) : {};
    const updatedUser = {
      user: {
        email: parsedUser?.user?.email || "",
        insightsRole: user?.user?.insightsRole || parsedUser?.user?.insightsRole || "",
        availableClients: user?.user?.availableClients || parsedUser?.user?.availableClients || {},
        current_client: user?.user?.current_client || parsedUser?.user?.current_client || {},
        webUserId: parsedUser?.user?.webUserId || {},
      },
      access_token: parsedUser?.access_token || "",
      refresh_token: parsedUser?.refresh_token || "",
    };

    const isUserAdmin = determineUserRole(updatedUser);

    const finalUser = {
      ...updatedUser,
      isUserAdmin,
    };

    localStorage.setItem("usmonUser", JSON.stringify(finalUser));
    setUser(finalUser);
    setIsAuthenticated(!!finalUser.access_token);
  }
};


  const catalogData = (data: any) => {
    localStorage.setItem("usmonCatalog", JSON.stringify(data));
    setCatalog(data);
  };

  const logout = () => {
    localStorage.removeItem("usmonUser");
    localStorage.removeItem("usmonCatalog");
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, catalogData,catalog,user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
